import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  AppBar,
  Toolbar,
  Container,
  List,
  ListItem,
  ListItemText,
  Popper,
  ClickAwayListener,
  Card,
  Avatar,
  Popover,
  Grid,
  Slider,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CheckIcon from '@mui/icons-material/Check';

const ROWS = 50;
const COLS = 26; // A to Z
const DEFAULT_COL_WIDTH = 100;
const DEFAULT_ROW_HEIGHT = 32;
const MIN_COL_WIDTH = 50;
const MAX_COL_WIDTH = 500;
const MIN_ROW_HEIGHT = 24;
const MAX_ROW_HEIGHT = 200;

// Predefined colors for color pickers
const COLORS = [
  '#FFFFFF', '#F5F5F5', '#F0F8FF', '#FFFACD', '#FFE4E1', '#E6E6FA', '#D3FFCE', '#FFFFCC', '#FFF0F5', '#FAEBD7',
  '#000000', '#808080', '#A9A9A9', '#D3D3D3', '#FF0000', '#FFA500', '#FFFF00', '#008000', '#0000FF', '#4B0082',
  '#800080', '#FFC0CB', '#FF69B4', '#FF1493', '#8B0000', '#FF8C00', '#FFD700', '#006400', '#00008B', '#483D8B',
  '#4682B4', '#00CED1', '#20B2AA', '#7FFFD4', '#00FA9A', '#3CB371', '#2E8B57', '#8FBC8F', '#9ACD32', '#6B8E23',
];

// Font size options
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

const SheetEditor = ({ sheet, onBack }) => {
  // Initialize empty grid data
  const [gridData, setGridData] = useState(() => {
    const rows = [];
    for (let i = 0; i < ROWS; i++) {
      const row = [];
      for (let j = 0; j < COLS; j++) {
        row.push('');
      }
      rows.push(row);
    }
    return rows;
  });
  
  // Cell formatting state
  const [cellFormatting, setCellFormatting] = useState(() => {
    const formatting = {};
    for (let i = 0; i < ROWS; i++) {
      formatting[i] = {};
      for (let j = 0; j < COLS; j++) {
        formatting[i][j] = {
          bold: false,
          italic: false,
          underline: false,
          backgroundColor: null,
          textColor: null,
          fontSize: 14, // Default font size
        };
      }
    }
    return formatting;
  });

  const [selectedCell, setSelectedCell] = useState(null);
  const [cellValue, setCellValue] = useState('');
  const [clipboard, setClipboard] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const cellInputRef = useRef(null);
  
  // Formatting popover states
  const [bgColorAnchorEl, setBgColorAnchorEl] = useState(null);
  const [textColorAnchorEl, setTextColorAnchorEl] = useState(null);
  const [fontSizeAnchorEl, setFontSizeAnchorEl] = useState(null);
  
  // Prospect mention states
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionAnchorEl, setMentionAnchorEl] = useState(null);
  const [mentionLoading, setMentionLoading] = useState(false);
  
  // Column widths and row heights
  const [columnWidths, setColumnWidths] = useState(() => {
    const widths = {};
    for (let i = 0; i < COLS; i++) {
      widths[i] = DEFAULT_COL_WIDTH;
    }
    return widths;
  });
  
  const [rowHeights, setRowHeights] = useState(() => {
    const heights = {};
    for (let i = 0; i < ROWS; i++) {
      heights[i] = DEFAULT_ROW_HEIGHT;
    }
    return heights;
  });
  
  // Resizing state
  const [resizing, setResizing] = useState(null);
  const [startPosition, setStartPosition] = useState(null);
  const [startSize, setStartSize] = useState(null);
  
  // Refs for event handling
  const tableRef = useRef(null);

  // Get column header (A, B, C, ...)
  const getColumnLabel = (index) => {
    return String.fromCharCode(65 + index);
  };

  // Handle cell selection
  const handleCellClick = (rowIndex, colIndex) => {
    setSelectedCell({ row: rowIndex, col: colIndex });
    setCellValue(gridData[rowIndex][colIndex]);
    
    // Close any open formatting popovers
    setBgColorAnchorEl(null);
    setTextColorAnchorEl(null);
    setFontSizeAnchorEl(null);
  };
  
  // Handle double click to start editing directly in cell
  const handleCellDoubleClick = (rowIndex, colIndex) => {
    setSelectedCell({ row: rowIndex, col: colIndex });
    setEditingCell({ row: rowIndex, col: colIndex });
    setCellValue(gridData[rowIndex][colIndex]);
    // Focus will be handled by useEffect when editingCell changes
  };

  // Handle cell value change
  const handleCellChange = (e) => {
    const value = e.target.value;
    setCellValue(value);
    
    if (selectedCell) {
      const newGridData = [...gridData];
      newGridData[selectedCell.row][selectedCell.col] = value;
      setGridData(newGridData);
      
      // Check for @ mentions
      handleMentionCheck(value, e.target);
    }
  };
  
  // Check for @ mentions in the cell value
  const handleMentionCheck = (value, element) => {
    // Check if the value contains an @ symbol
    const atIndex = value.lastIndexOf('@');
    
    if (atIndex !== -1) {
      // Get the text after the @ symbol
      const searchText = value.substring(atIndex + 1).trim();
      
      if (searchText.length > 0) {
        // Set mention active and search text
        setMentionActive(true);
        setMentionSearch(searchText);
        setMentionAnchorEl(element);
        
        // Search for prospects
        searchProspects(searchText);
      } else {
        setMentionActive(true);
        setMentionSearch('');
        setMentionAnchorEl(element);
        setMentionResults([]);
      }
    } else {
      // No @ symbol, reset mention state
      setMentionActive(false);
      setMentionSearch('');
      setMentionResults([]);
    }
  };
  
  // Search for prospects by first name
  const searchProspects = async (searchText) => {
    try {
      setMentionLoading(true);
      
      // Make API call to search for prospects
      const response = await axios.get('/prospects', {
        params: {
          search: searchText,
          sort: '-createdAt', // Sort by most recently added
          limit: 10 // Limit to 10 results
        }
      });
      
      // Set the search results
      setMentionResults(response.data.data || []);
    } catch (error) {
      console.error('Error searching prospects:', error);
      setMentionResults([]);
    } finally {
      setMentionLoading(false);
    }
  };
  
  // Handle prospect selection
  const handleProspectSelect = (prospect) => {
    if (selectedCell && editingCell) {
      const { row, col } = selectedCell;
      
      // Create a new grid data to update multiple cells
      const newGridData = [...gridData];
      
      // Clear the current cell value (remove the @mention)
      const currentValue = cellValue;
      const atIndex = currentValue.lastIndexOf('@');
      
      if (atIndex !== -1) {
        // Clear the @mention from the current cell
        const newValue = currentValue.substring(0, atIndex).trim();
        setCellValue(newValue);
        newGridData[row][col] = newValue;
      }
      
      // Populate cells with prospect data in the following order:
      // First name, last name, position, height, weight, HUDL link, highschool, city, state, graduation year
      
      // Make sure we don't go beyond the grid boundaries
      const maxCol = Math.min(col + 9, COLS - 1);
      
      // First name (current cell)
      newGridData[row][col] = prospect.firstName || 'N/A';
      
      // Last name (col + 1)
      if (col + 1 <= maxCol) {
        newGridData[row][col + 1] = prospect.lastName || 'N/A';
      }
      
      // Position (col + 2)
      if (col + 2 <= maxCol) {
        newGridData[row][col + 2] = prospect.position || 'N/A';
      }
      
      // Height (col + 3)
      if (col + 3 <= maxCol) {
        newGridData[row][col + 3] = prospect.height || 'N/A';
      }
      
      // Weight (col + 4)
      if (col + 4 <= maxCol) {
        newGridData[row][col + 4] = prospect.weight ? prospect.weight.toString() : 'N/A';
      }
      
      // HUDL link (col + 5)
      if (col + 5 <= maxCol) {
        const hudlLink = prospect.socialMedia && prospect.socialMedia.hudl && 
                        prospect.socialMedia.hudl.length > 0 ? 
                        prospect.socialMedia.hudl[0] : 'N/A';
        newGridData[row][col + 5] = hudlLink;
      }
      
      // High school (col + 6)
      if (col + 6 <= maxCol) {
        newGridData[row][col + 6] = prospect.highSchool || 'N/A';
      }
      
      // City (col + 7)
      if (col + 7 <= maxCol) {
        newGridData[row][col + 7] = prospect.address && prospect.address.city ? 
                                   prospect.address.city : 'N/A';
      }
      
      // State (col + 8)
      if (col + 8 <= maxCol) {
        newGridData[row][col + 8] = prospect.address && prospect.address.state ? 
                                   prospect.address.state : 'N/A';
      }
      
      // Graduation year (col + 9)
      if (col + 9 <= maxCol) {
        newGridData[row][col + 9] = prospect.graduationYear ? 
                                   prospect.graduationYear.toString() : 'N/A';
      }
      
      // Update the grid data with all the prospect information
      setGridData(newGridData);
      
      // Move selection to the last populated cell
      setSelectedCell({ row, col });
      setEditingCell(null); // Exit editing mode
    }
    
    // Reset mention state
    setMentionActive(false);
    setMentionSearch('');
    setMentionResults([]);
  };
  
  // Save current cell and move to next cell
  const saveAndMoveToNextCell = () => {
    if (!selectedCell) return;
    
    // Save current value
    const newGridData = [...gridData];
    newGridData[selectedCell.row][selectedCell.col] = cellValue;
    setGridData(newGridData);
    
    // Move to next cell (down)
    const nextRow = Math.min(ROWS - 1, selectedCell.row + 1);
    setSelectedCell({ row: nextRow, col: selectedCell.col });
    setCellValue(gridData[nextRow][selectedCell.col]);
    setEditingCell(null); // Exit editing mode
  };
  
  // Handle key press in cell input
  const handleCellInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveAndMoveToNextCell();
      return;
    }
    
    // Handle arrow key navigation when editing
    let newRow = selectedCell.row;
    let newCol = selectedCell.col;
    let shouldNavigate = false;
    
    switch (e.key) {
      case 'ArrowUp':
        if (e.ctrlKey || e.metaKey) {
          // Allow normal cursor movement within the cell when holding ctrl/cmd
          return;
        }
        newRow = Math.max(0, selectedCell.row - 1);
        shouldNavigate = true;
        break;
      case 'ArrowDown':
        if (e.ctrlKey || e.metaKey) {
          // Allow normal cursor movement within the cell when holding ctrl/cmd
          return;
        }
        newRow = Math.min(ROWS - 1, selectedCell.row + 1);
        shouldNavigate = true;
        break;
      case 'ArrowLeft':
        // Only navigate if cursor is at the beginning of the text
        if (e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
          newCol = Math.max(0, selectedCell.col - 1);
          shouldNavigate = true;
        }
        break;
      case 'ArrowRight':
        // Only navigate if cursor is at the end of the text
        if (e.target.selectionStart === e.target.value.length && 
            e.target.selectionEnd === e.target.value.length) {
          newCol = Math.min(COLS - 1, selectedCell.col + 1);
          shouldNavigate = true;
        }
        break;
    }
    
    if (shouldNavigate) {
      e.preventDefault();
      
      // Save the current cell value
      const newGridData = [...gridData];
      newGridData[selectedCell.row][selectedCell.col] = cellValue;
      setGridData(newGridData);
      
      // Move to the new cell
      setSelectedCell({ row: newRow, col: newCol });
      setCellValue(gridData[newRow][newCol]);
      setEditingCell({ row: newRow, col: newCol });
    }
  };

  // Handle key navigation
  const handleKeyDown = (e, rowIndex, colIndex) => {
    if (!selectedCell) return;
    
    // If we're not in edit mode and user presses a character key, start editing
    if (!editingCell && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      setEditingCell({ row: selectedCell.row, col: selectedCell.col });
      // Don't set cellValue here as we want to replace the current value
      // The keypress will be handled by the input once it's focused
      return;
    }

    let newRow = selectedCell.row;
    let newCol = selectedCell.col;

    switch (e.key) {
      case 'ArrowUp':
        newRow = Math.max(0, selectedCell.row - 1);
        break;
      case 'ArrowDown':
        newRow = Math.min(ROWS - 1, selectedCell.row + 1);
        break;
      case 'ArrowLeft':
        newCol = Math.max(0, selectedCell.col - 1);
        break;
      case 'ArrowRight':
        newCol = Math.min(COLS - 1, selectedCell.col + 1);
        break;
      case 'Tab':
        e.preventDefault();
        if (editingCell) {
          // Save current value before tabbing
          const newGridData = [...gridData];
          newGridData[selectedCell.row][selectedCell.col] = cellValue;
          setGridData(newGridData);
          setEditingCell(null);
        }
        newCol = (selectedCell.col + 1) % COLS;
        if (newCol === 0) {
          newRow = Math.min(ROWS - 1, selectedCell.row + 1);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (editingCell) {
          saveAndMoveToNextCell();
          return;
        }
        newRow = Math.min(ROWS - 1, selectedCell.row + 1);
        break;
      case 'Escape':
        if (editingCell) {
          // Cancel editing and revert to original value
          setCellValue(gridData[selectedCell.row][selectedCell.col]);
          setEditingCell(null);
          return;
        }
        break;
      case 'F2':
        // F2 key starts editing (Excel behavior)
        if (!editingCell) {
          setEditingCell({ row: selectedCell.row, col: selectedCell.col });
          return;
        }
        break;
      default:
        return;
    }

    if (newRow !== selectedCell.row || newCol !== selectedCell.col) {
      setSelectedCell({ row: newRow, col: newCol });
      setCellValue(gridData[newRow][newCol]);
    }
  };

  // Context menu handlers
  const handleContextMenu = (event, rowIndex, colIndex) => {
    event.preventDefault();
    setSelectedCell({ row: rowIndex, col: colIndex });
    setCellValue(gridData[rowIndex][colIndex]);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Clipboard operations
  const handleCopy = () => {
    if (selectedCell) {
      setClipboard({
        type: 'single',
        value: gridData[selectedCell.row][selectedCell.col]
      });
      handleCloseContextMenu();
    }
  };

  const handleCut = () => {
    if (selectedCell) {
      setClipboard({
        type: 'single',
        value: gridData[selectedCell.row][selectedCell.col]
      });
      
      const newGridData = [...gridData];
      newGridData[selectedCell.row][selectedCell.col] = '';
      setGridData(newGridData);
      setCellValue('');
      handleCloseContextMenu();
    }
  };

  const handlePaste = () => {
    if (selectedCell && clipboard) {
      const newGridData = [...gridData];
      newGridData[selectedCell.row][selectedCell.col] = clipboard.value;
      setGridData(newGridData);
      setCellValue(clipboard.value);
      handleCloseContextMenu();
    }
  };

  // Column resizing handlers
  const handleColumnResizeStart = (e, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ type: 'column', index: colIndex });
    setStartPosition(e.clientX);
    setStartSize(columnWidths[colIndex]);
    
    // Add event listeners for mousemove and mouseup
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  
  // Row resizing handlers
  const handleRowResizeStart = (e, rowIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ type: 'row', index: rowIndex });
    setStartPosition(e.clientY);
    setStartSize(rowHeights[rowIndex]);
    
    // Add event listeners for mousemove and mouseup
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  
  const handleResizeMove = (e) => {
    if (!resizing || startPosition === null || startSize === null) return;
    
    if (resizing.type === 'column') {
      const diff = e.clientX - startPosition;
      const newWidth = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, startSize + diff));
      
      setColumnWidths(prev => ({
        ...prev,
        [resizing.index]: newWidth
      }));
    } else if (resizing.type === 'row') {
      const diff = e.clientY - startPosition;
      const newHeight = Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, startSize + diff));
      
      setRowHeights(prev => ({
        ...prev,
        [resizing.index]: newHeight
      }));
    }
  };
  
  const handleResizeEnd = () => {
    setResizing(null);
    setStartPosition(null);
    setStartSize(null);
    
    // Remove event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };
  
  // Focus the cell input when editing cell changes
  useEffect(() => {
    if (editingCell && cellInputRef.current) {
      cellInputRef.current.focus();
      cellInputRef.current.select(); // Select all text for easy replacement
    }
  }, [editingCell]);
  
  // Close mention dropdown when clicking outside
  const handleClickAway = () => {
    setMentionActive(false);
    setMentionSearch('');
    setMentionResults([]);
  };
  
  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, []);
  
  // Formatting handlers
  const handleToggleBold = () => {
    if (!selectedCell) return;
    
    const { row, col } = selectedCell;
    const newFormatting = { ...cellFormatting };
    newFormatting[row][col] = {
      ...newFormatting[row][col],
      bold: !newFormatting[row][col].bold
    };
    
    setCellFormatting(newFormatting);
  };
  
  const handleToggleItalic = () => {
    if (!selectedCell) return;
    
    const { row, col } = selectedCell;
    const newFormatting = { ...cellFormatting };
    newFormatting[row][col] = {
      ...newFormatting[row][col],
      italic: !newFormatting[row][col].italic
    };
    
    setCellFormatting(newFormatting);
  };
  
  const handleToggleUnderline = () => {
    if (!selectedCell) return;
    
    const { row, col } = selectedCell;
    const newFormatting = { ...cellFormatting };
    newFormatting[row][col] = {
      ...newFormatting[row][col],
      underline: !newFormatting[row][col].underline
    };
    
    setCellFormatting(newFormatting);
  };
  
  const handleSetBackgroundColor = (color) => {
    if (!selectedCell) return;
    
    const { row, col } = selectedCell;
    const newFormatting = { ...cellFormatting };
    newFormatting[row][col] = {
      ...newFormatting[row][col],
      backgroundColor: color
    };
    
    setCellFormatting(newFormatting);
    setBgColorAnchorEl(null); // Close the popover
  };
  
  const handleSetTextColor = (color) => {
    if (!selectedCell) return;
    
    const { row, col } = selectedCell;
    const newFormatting = { ...cellFormatting };
    newFormatting[row][col] = {
      ...newFormatting[row][col],
      textColor: color
    };
    
    setCellFormatting(newFormatting);
    setTextColorAnchorEl(null); // Close the popover
  };
  
  const handleSetFontSize = (size) => {
    if (!selectedCell) return;
    
    const { row, col } = selectedCell;
    const newFormatting = { ...cellFormatting };
    newFormatting[row][col] = {
      ...newFormatting[row][col],
      fontSize: size
    };
    
    setCellFormatting(newFormatting);
    setFontSizeAnchorEl(null); // Close the popover
  };
  
  // Save sheet data
  const handleSave = () => {
    console.log('Saving sheet:', sheet.name, gridData, columnWidths, rowHeights, cellFormatting);
    // In a real app, this would save to backend
    alert('Sheet saved successfully!');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, px: 0 }}>
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton edge="start" onClick={onBack} sx={{ mr: 2, color: '#FFFFFF' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#FFFFFF' }}>
            {sheet.name}
          </Typography>
          <Button 
            startIcon={<SaveIcon />} 
            variant="contained" 
            color="primary"
            onClick={handleSave}
          >
            Save
          </Button>
        </Toolbar>
      </AppBar>

      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton size="small" onClick={handleCut} disabled={!selectedCell}>
              <ContentCutIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleCopy} disabled={!selectedCell}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handlePaste} disabled={!clipboard}>
              <ContentPasteIcon fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton 
              size="small" 
              onClick={handleToggleBold} 
              disabled={!selectedCell}
              color={selectedCell && cellFormatting[selectedCell.row][selectedCell.col].bold ? 'primary' : 'default'}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleToggleItalic} 
              disabled={!selectedCell}
              color={selectedCell && cellFormatting[selectedCell.row][selectedCell.col].italic ? 'primary' : 'default'}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleToggleUnderline} 
              disabled={!selectedCell}
              color={selectedCell && cellFormatting[selectedCell.row][selectedCell.col].underline ? 'primary' : 'default'}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton 
              size="small" 
              onClick={(e) => setBgColorAnchorEl(e.currentTarget)} 
              disabled={!selectedCell}
            >
              <FormatColorFillIcon fontSize="small" />
              {selectedCell && cellFormatting[selectedCell.row][selectedCell.col].backgroundColor && (
                <Box 
                  sx={{ 
                    width: 10, 
                    height: 10, 
                    position: 'absolute', 
                    bottom: 2, 
                    right: 2, 
                    backgroundColor: cellFormatting[selectedCell.row][selectedCell.col].backgroundColor,
                    border: '1px solid #ccc'
                  }} 
                />
              )}
            </IconButton>
            <IconButton 
              size="small" 
              onClick={(e) => setTextColorAnchorEl(e.currentTarget)} 
              disabled={!selectedCell}
            >
              <FormatColorTextIcon fontSize="small" />
              {selectedCell && cellFormatting[selectedCell.row][selectedCell.col].textColor && (
                <Box 
                  sx={{ 
                    width: 10, 
                    height: 10, 
                    position: 'absolute', 
                    bottom: 2, 
                    right: 2, 
                    backgroundColor: cellFormatting[selectedCell.row][selectedCell.col].textColor,
                    border: '1px solid #ccc'
                  }} 
                />
              )}
            </IconButton>
            <IconButton 
              size="small" 
              onClick={(e) => setFontSizeAnchorEl(e.currentTarget)} 
              disabled={!selectedCell}
            >
              <FormatSizeIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Cell input */}
        {selectedCell && (
          <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              size="small"
              fullWidth
              value={cellValue}
              onChange={handleCellChange}
              onKeyDown={handleCellInputKeyDown}
              placeholder={`${getColumnLabel(selectedCell.col)}${selectedCell.row + 1}`}
              variant="outlined"
              autoFocus
            />
          </Box>
        )}

        {/* Spreadsheet grid */}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }} ref={tableRef}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 50, position: 'sticky', left: 0, zIndex: 2 }}></TableCell>
                {Array.from({ length: COLS }).map((_, index) => (
                  <TableCell 
                    key={index} 
                    align="center"
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      fontWeight: 'bold',
                      width: `${columnWidths[index]}px`,
                      minWidth: `${columnWidths[index]}px`,
                      maxWidth: `${columnWidths[index]}px`,
                      position: 'relative',
                      userSelect: 'none'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {getColumnLabel(index)}
                      <Tooltip title="Drag to resize column" placement="top">
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '8px',
                            cursor: 'col-resize',
                            backgroundColor: '#e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              backgroundColor: '#1976d2',
                            },
                            '&::after': {
                              content: '""',
                              height: '50%',
                              width: '2px',
                              backgroundColor: '#9e9e9e',
                            }
                          }}
                          onMouseDown={(e) => handleColumnResizeStart(e, index)}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {gridData.map((row, rowIndex) => (
                <TableRow 
                  key={rowIndex}
                  sx={{ height: rowHeights[rowIndex] }}
                >
                  <TableCell 
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      height: rowHeights[rowIndex],
                      position: 'relative',
                      userSelect: 'none'
                    }}
                    align="center"
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {rowIndex + 1}
                      <Tooltip title="Drag to resize row" placement="right">
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: '8px',
                            cursor: 'row-resize',
                            backgroundColor: '#e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              backgroundColor: '#1976d2',
                            },
                            '&::after': {
                              content: '""',
                              width: '50%',
                              height: '2px',
                              backgroundColor: '#9e9e9e',
                            }
                          }}
                          onMouseDown={(e) => handleRowResizeStart(e, rowIndex)}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {row.map((cell, colIndex) => (
                    <TableCell 
                      key={colIndex}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                      onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
                      onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                      onContextMenu={(e) => handleContextMenu(e, rowIndex, colIndex)}
                      tabIndex={0}
                      sx={{
                        position: 'relative',
                        cursor: 'cell',
                        p: editingCell && editingCell.row === rowIndex && editingCell.col === colIndex ? 0 : 1,
                        border: '1px solid #e0e0e0',
                        outline: selectedCell && selectedCell.row === rowIndex && selectedCell.col === colIndex
                          ? '2px solid #1976d2'
                          : 'none',
                        width: `${columnWidths[colIndex]}px`,
                        minWidth: `${columnWidths[colIndex]}px`,
                        maxWidth: `${columnWidths[colIndex]}px`,
                        height: `${rowHeights[rowIndex]}px`,
                        minHeight: `${rowHeights[rowIndex]}px`,
                        maxHeight: `${rowHeights[rowIndex]}px`,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        // Apply cell formatting
                        backgroundColor: cellFormatting[rowIndex][colIndex].backgroundColor || 'inherit',
                        color: cellFormatting[rowIndex][colIndex].textColor || 'inherit',
                        fontWeight: cellFormatting[rowIndex][colIndex].bold ? 'bold' : 'normal',
                        fontStyle: cellFormatting[rowIndex][colIndex].italic ? 'italic' : 'normal',
                        textDecoration: cellFormatting[rowIndex][colIndex].underline ? 'underline' : 'none',
                        fontSize: `${cellFormatting[rowIndex][colIndex].fontSize}px`,
                      }}
                    >
                      {editingCell && editingCell.row === rowIndex && editingCell.col === colIndex ? (
                        <TextField
                          fullWidth
                          variant="standard"
                          value={cellValue}
                          onChange={handleCellChange}
                          onKeyDown={handleCellInputKeyDown}
                          inputRef={cellInputRef}
                          autoFocus
                          placeholder="Type @ to mention a prospect"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '100%',
                              padding: '0 4px',
                              // Apply cell formatting to input
                              backgroundColor: cellFormatting[rowIndex][colIndex].backgroundColor || 'inherit',
                              color: cellFormatting[rowIndex][colIndex].textColor || 'inherit',
                              fontWeight: cellFormatting[rowIndex][colIndex].bold ? 'bold' : 'normal',
                              fontStyle: cellFormatting[rowIndex][colIndex].italic ? 'italic' : 'normal',
                              textDecoration: cellFormatting[rowIndex][colIndex].underline ? 'underline' : 'none',
                              fontSize: `${cellFormatting[rowIndex][colIndex].fontSize}px`,
                            },
                            '& .MuiInputBase-input': {
                              height: '100%',
                              padding: '0',
                            },
                            '&:before, &:after': {
                              display: 'none',
                            },
                          }}
                          InputProps={{
                            disableUnderline: true,
                          }}
                        />
                      ) : (
                        cell
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleCut} disabled={!selectedCell}>Cut</MenuItem>
        <MenuItem onClick={handleCopy} disabled={!selectedCell}>Copy</MenuItem>
        <MenuItem onClick={handlePaste} disabled={!clipboard}>Paste</MenuItem>
      </Menu>
      
      {/* Prospect Mention Dropdown */}
      {mentionActive && mentionAnchorEl && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Popper
            open={mentionActive}
            anchorEl={mentionAnchorEl}
            placement="bottom-start"
            style={{ zIndex: 1300, width: 300, maxHeight: 400, overflow: 'auto' }}
          >
            <Card elevation={3} sx={{ p: 1 }}>
              {mentionLoading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2">Loading prospects...</Typography>
                </Box>
              ) : mentionResults.length > 0 ? (
                <List dense>
                  {mentionResults.map((prospect) => (
                    <ListItem 
                      key={prospect._id} 
                      button 
                      onClick={() => handleProspectSelect(prospect)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <Avatar 
                        src={prospect.profilePicture || ''} 
                        sx={{ width: 32, height: 32, mr: 1 }}
                      >
                        {prospect.firstName[0]}{prospect.lastName[0]}
                      </Avatar>
                      <ListItemText 
                        primary={`${prospect.firstName} ${prospect.lastName}`} 
                        secondary={prospect.position || 'No position'} 
                      />
                    </ListItem>
                  ))}
                </List>
              ) : mentionSearch ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2">No prospects found starting with "{mentionSearch}"</Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2">Type to search for prospects</Typography>
                </Box>
              )}
            </Card>
          </Popper>
        </ClickAwayListener>
      )}
      
      {/* Background Color Picker */}
      <Popover
        open={Boolean(bgColorAnchorEl)}
        anchorEl={bgColorAnchorEl}
        onClose={() => setBgColorAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Background Color</Typography>
          <Grid container spacing={1}>
            {COLORS.map((color) => (
              <Grid item key={color}>
                <Box
                  onClick={() => handleSetBackgroundColor(color)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 0 5px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  {selectedCell && 
                   cellFormatting[selectedCell.row][selectedCell.col].backgroundColor === color && (
                    <CheckIcon 
                      sx={{ 
                        position: 'absolute', 
                        fontSize: 16,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: color === '#FFFFFF' ? '#000000' : '#FFFFFF'
                      }} 
                    />
                  )}
                </Box>
              </Grid>
            ))}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Button 
                fullWidth 
                size="small" 
                variant="outlined" 
                onClick={() => handleSetBackgroundColor(null)}
              >
                Clear Color
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Popover>
      
      {/* Text Color Picker */}
      <Popover
        open={Boolean(textColorAnchorEl)}
        anchorEl={textColorAnchorEl}
        onClose={() => setTextColorAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Text Color</Typography>
          <Grid container spacing={1}>
            {COLORS.map((color) => (
              <Grid item key={color}>
                <Box
                  onClick={() => handleSetTextColor(color)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 0 5px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  {selectedCell && 
                   cellFormatting[selectedCell.row][selectedCell.col].textColor === color && (
                    <CheckIcon 
                      sx={{ 
                        position: 'absolute', 
                        fontSize: 16,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: color === '#FFFFFF' ? '#000000' : '#FFFFFF'
                      }} 
                    />
                  )}
                </Box>
              </Grid>
            ))}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Button 
                fullWidth 
                size="small" 
                variant="outlined" 
                onClick={() => handleSetTextColor(null)}
              >
                Default Color
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Popover>
      
      {/* Font Size Selector */}
      <Popover
        open={Boolean(fontSizeAnchorEl)}
        anchorEl={fontSizeAnchorEl}
        onClose={() => setFontSizeAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 180 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Font Size</Typography>
          <List dense>
            {FONT_SIZES.map((size) => (
              <ListItem 
                key={size} 
                button 
                onClick={() => handleSetFontSize(size)}
                selected={selectedCell && cellFormatting[selectedCell.row][selectedCell.col].fontSize === size}
              >
                <ListItemText primary={`${size}px`} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Container>
  );
};

export default SheetEditor;
